import { to } from "react-lib/utils";
import BaseService from "react-lib/apis/services/BaseService";
import config from "../../config/config";

const HOST = `${config.PODCAST_API_URL}`;

type RequestParams = {
  endpoint: string;
  params?: Record<string, any>;
  apiToken?: string;
  extra?: Record<string, any>;
};

type RequestWithDataParams<D = any> = RequestParams & {
  data?: D;
};

type ApiResponse<T> = [T | null, any, any];

const setAuth = (base: BaseService, apiToken?: string) => {
  if (apiToken) {
    base.client.defaults.headers.common["Authorization"] = `Token ${apiToken}`;
  }
};

const setContentType = (base: BaseService, data: any) => {
  if (data instanceof URLSearchParams) {
    base.client.defaults.headers.common["Content-Type"] = "application/x-www-form-urlencoded";
  } else if (data instanceof FormData) {
    base.client.defaults.headers.common["Content-Type"] = "multipart/form-data";
  }
};

const PodcastAPIRequestView = {
  get: async <T = any>(params: RequestParams): Promise<ApiResponse<T>> => {
    const base = new BaseService();
    setAuth(base, params.apiToken);

    const result = await to(
      base.client
        .get(`${HOST}${params.endpoint}`, {
          params: params.params ? { ...params.params } : {},
          ...params.extra,
        })
        .then(base.handleResponse)
        .catch(base.throwErrorMessage),
    );

    return [result[1], result[0], result[2]];
  },

  post: async <T = any, D = any>(params: RequestWithDataParams<D>): Promise<ApiResponse<T>> => {
    const base = new BaseService();
    setAuth(base, params.apiToken);
    setContentType(base, params.data);

    const result = await to(
      base.client
        .post(`${HOST}${params.endpoint}`, params.data, {
          params: params.params ? { ...params.params } : {},
          ...params.extra,
        })
        .then(base.handleResponse)
        .catch(base.throwErrorMessage),
    );

    return [result[1], result[0], result[2]];
  },

  patch: async <T = any, D = any>(params: RequestWithDataParams<D>): Promise<ApiResponse<T>> => {
    const base = new BaseService();
    setAuth(base, params.apiToken);
    setContentType(base, params.data);

    const result = await to(
      base.client
        .patch(`${HOST}${params.endpoint}`, params.data, {
          params: params.params ? { ...params.params } : {},
          ...params.extra,
        })
        .then(base.handleResponse)
        .catch(base.throwErrorMessage),
    );

    return [result[1], result[0], result[2]];
  },

  put: async <T = any, D = any>(params: RequestWithDataParams<D>): Promise<ApiResponse<T>> => {
    const base = new BaseService();
    setAuth(base, params.apiToken);
    setContentType(base, params.data);

    const result = await to(
      base.client
        .put(`${HOST}${params.endpoint}`, params.data, {
          params: params.params ? { ...params.params } : {},
          ...params.extra,
        })
        .then(base.handleResponse)
        .catch(base.throwErrorMessage),
    );

    return [result[1], result[0], result[2]];
  },

  delete: async <T = any>(params: RequestParams): Promise<ApiResponse<T>> => {
    const base = new BaseService();
    setAuth(base, params.apiToken);

    const result = await to(
      base.client
        .delete(`${HOST}${params.endpoint}`, {
          params: params.params ? { ...params.params } : {},
          ...params.extra,
        })
        .then(base.handleResponse)
        .catch(base.throwErrorMessage),
    );

    return [result[1], result[0], result[2]];
  },
};

export default PodcastAPIRequestView;
